import { __assign, __read } from "tslib";
import React, { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@keen.io/icons';
import { Text, DynamicPortal } from '@keen.io/ui-core';
import { formatValue as valueFormatter, } from '@keen.io/charts-utils';
import MetricIcon from './metric-icon.component';
import { Excerpt, Wrapper, TextWrapper, ValueContainer, IconWrapper, Layout, } from './metric-chart.styles';
import { generateMetric, createMargins } from './utils';
import { theme as defaultTheme } from '../../theme';
import { MetricTooltip } from './components';
export var textMotion = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.3, delay: 0.2 },
    exit: {},
};
export var decreaseMotion = {
    initial: { opacity: 0.5, y: -5 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.2 },
    exit: {},
};
export var increaseMotion = {
    initial: { opacity: 0.5, y: 5 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.2 },
    exit: {},
};
export var MetricChart = function (_a) {
    var data = _a.data, valuePrefix = _a.valuePrefix, valueSuffix = _a.valueSuffix, formatValue = _a.formatValue, _b = _a.labelSelector, labelSelector = _b === void 0 ? 'name' : _b, _c = _a.theme, theme = _c === void 0 ? defaultTheme : _c, _d = _a.keys, keys = _d === void 0 ? ['value'] : _d, _e = _a.type, type = _e === void 0 ? 'simple' : _e, _f = _a.usePercentDifference, usePercentDifference = _f === void 0 ? false : _f, secondaryValueDescription = _a.secondaryValueDescription;
    var _g = generateMetric({
        labelSelector: labelSelector,
        keys: keys,
        data: data,
        type: type,
        usePercentDifference: usePercentDifference,
    }), value = _g.value, previousValue = _g.previousValue, difference = _g.difference;
    var _h = theme.metric, excerpt = _h.excerpt, valueSettings = _h.value, icon = _h.icon, prefix = _h.prefix, suffix = _h.suffix;
    var statusIcon = __assign({}, ((difference === null || difference === void 0 ? void 0 : difference.status) === 'increase'
        ? excerpt.icons.increase
        : excerpt.icons.decrease));
    var requestFrameRef = useRef(null);
    var tooltipSettings = theme.tooltip;
    var excerptValue = type === 'difference' ? difference.value : previousValue;
    var _j = __read(useState(false), 2), tooltipVisible = _j[0], setTooltipVisible = _j[1];
    var _k = __read(useState({ x: 0, y: 0 }), 2), tooltipPosition = _k[0], setTooltipPosition = _k[1];
    return (React.createElement(Layout, null,
        React.createElement(ValueContainer, null,
            React.createElement(AnimatePresence, null,
                React.createElement(motion.div, __assign({}, textMotion),
                    React.createElement(TextWrapper, null,
                        valuePrefix && React.createElement(Text, __assign({}, prefix.typography), valuePrefix),
                        React.createElement(Text, __assign({}, valueSettings.typography), valueFormatter(value, formatValue)),
                        valueSuffix && React.createElement(Text, __assign({}, suffix.typography), valueSuffix)))),
            secondaryValueDescription && tooltipVisible && (React.createElement(DynamicPortal, null,
                React.createElement(MetricTooltip, { tooltipPosition: tooltipPosition, tooltipSettings: tooltipSettings },
                    React.createElement(Text, __assign({}, tooltipSettings.labels.typography), secondaryValueDescription)))),
            difference && (React.createElement("div", null,
                React.createElement(Excerpt, { "data-testid": "metric-excerpt-value", background: excerpt.backgroundColor, onMouseEnter: function (e) {
                        setTooltipVisible(true);
                        setTooltipPosition({ x: e.clientX, y: e.clientY });
                    }, onMouseMove: function (e) {
                        e.persist();
                        if (requestFrameRef.current)
                            cancelAnimationFrame(requestFrameRef.current);
                        requestFrameRef.current = requestAnimationFrame(function () {
                            setTooltipPosition({ x: e.clientX, y: e.clientY });
                        });
                    }, onMouseLeave: function () {
                        setTooltipVisible(false);
                    } },
                    React.createElement(Wrapper, null,
                        type !== 'comparison' && difference.status !== 'static' && (React.createElement(AnimatePresence, null,
                            React.createElement(motion.div, __assign({}, (difference.status === 'increase'
                                ? increaseMotion
                                : decreaseMotion)),
                                React.createElement(IconWrapper, null,
                                    React.createElement(Icon, { type: statusIcon.type, fill: statusIcon.color }))))),
                        React.createElement(Text, __assign({}, excerpt.typography), type === 'difference' && usePercentDifference
                            ? excerptValue + "%"
                            : valueFormatter(excerptValue, formatValue))))))),
        icon.enabled && (React.createElement("div", { style: createMargins(icon.margins) },
            React.createElement(MetricIcon, { position: icon.position, circleStyle: icon.style, baseColor: valueSettings.typography.fontColor },
                React.createElement(Icon, { type: icon.type, width: 80, height: 80, opacity: icon.style === 'solid' ? 0.15 : 0.2, fill: valueSettings.typography.fontColor }))))));
};
export default MetricChart;
